// eslint-disable-next-line import/no-extraneous-dependencies
import InfiniteScroll from 'react-infinite-scroll-component';
import { useEffect, useState } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import BlogCard from '@/components/Blog/BlogCard';
import { useGetBlogsQuery } from '@/redux/blog/blog.api.slice';
import { IBlogListItem, IGetBlogsResponse } from '@/redux/blog/blog.d';

const PAGE_LIMIT = 12;

function isResponseReady(
  isLoading: boolean,
  isFetching: boolean,
  response: IGetBlogsResponse | undefined
): response is IGetBlogsResponse {
  if (isLoading) return false;
  if (isFetching) return false;
  return response !== undefined;
}

function mergePosts(
  prev: IBlogListItem[],
  items: IBlogListItem[]
): IBlogListItem[] {
  const seen = new Set(prev.map((p) => p._id));
  const newItems = items.filter((item) => !seen.has(item._id));
  return [...prev, ...newItems];
}

function isPresent(
  item: IBlogListItem | null | undefined
): item is IBlogListItem {
  return item !== null && item !== undefined;
}

function toCategoryKey(category?: string): string {
  return category ?? 'all';
}

function toCategoryParam(category?: string): string | undefined {
  return category || undefined;
}

interface BlogFeed {
  posts: IBlogListItem[];
  hasMore: boolean;
  isFilterLoading: boolean;
  isLoading: boolean;
  fetchMoreData: () => void;
}

function useBlogFeed(category?: string): BlogFeed {
  const [posts, setPosts] = useState<IBlogListItem[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // The category currently reflected in `posts`. Computed in render so the
  // skeleton shows the instant the selected tab changes (mirrors
  // FeaturedPostsList's filterKey trick).
  const selectedKey = toCategoryKey(category);
  const [loadedKey, setLoadedKey] = useState(selectedKey);

  const {
    data: response,
    isLoading,
    isFetching,
  } = useGetBlogsQuery({
    category: toCategoryParam(category),
    page,
    limit: PAGE_LIMIT,
  });

  const isFilterLoading = selectedKey !== loadedKey;

  // Reset paging whenever the selected category changes.
  useEffect(() => {
    setPage(1);
    setHasMore(true);
  }, [category]);

  useEffect(() => {
    if (!isResponseReady(isLoading, isFetching, response)) return;
    const items = response.items ?? [];
    if (page === 1) {
      setPosts(items);
      setLoadedKey(selectedKey);
    } else {
      setPosts((prev) => mergePosts(prev, items));
    }
    setHasMore(response.hasMore);
  }, [response, page, isLoading, isFetching, selectedKey]);

  const fetchMoreData = () => {
    setPage((prev) => prev + 1);
  };

  return { posts, hasMore, isFilterLoading, isLoading, fetchMoreData };
}

function isShowingSkeleton(
  isFilterLoading: boolean,
  isLoading: boolean
): boolean {
  if (isFilterLoading) return true;
  return isLoading;
}

function GridSkeletons({ count, prefix }: { count: number; prefix: string }) {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <Skeleton key={`${prefix}-${index}`} className="w-full h-[360px]" />
      ))}
    </>
  );
}

function BlogListSkeleton() {
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-5 py-4 w-full">
      <GridSkeletons count={9} prefix="blog-filter-skeleton" />
    </div>
  );
}

function BlogEmptyState() {
  return (
    <div className="w-full flex flex-col items-center justify-center py-24 text-center">
      <p className="text-lg font-medium font-primary text-gray-700">
        No posts yet
      </p>
      <p className="text-sm text-muted-foreground font-secondary mt-1">
        Check back soon for new articles.
      </p>
    </div>
  );
}

function BlogGrid({
  posts,
  hasMore,
  fetchMoreData,
}: {
  posts: IBlogListItem[];
  hasMore: boolean;
  fetchMoreData: () => void;
}) {
  return (
    <InfiniteScroll
      dataLength={posts.length}
      next={fetchMoreData}
      hasMore={hasMore}
      loader={<GridSkeletons count={3} prefix="blog-skeleton" />}
      className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-5 py-4 w-full"
      style={{ overflow: 'visible' }}
    >
      {posts.filter(isPresent).map((item) => (
        <BlogCard key={item._id} post={item} />
      ))}
    </InfiniteScroll>
  );
}

function BlogList({ category }: { category?: string }) {
  const { posts, hasMore, isFilterLoading, isLoading, fetchMoreData } =
    useBlogFeed(category);

  if (isShowingSkeleton(isFilterLoading, isLoading)) {
    return <BlogListSkeleton />;
  }

  if (posts.length === 0) {
    return <BlogEmptyState />;
  }

  return (
    <BlogGrid posts={posts} hasMore={hasMore} fetchMoreData={fetchMoreData} />
  );
}

export default BlogList;
